import React, { useState } from "react";
import { Sparkles, Accessibility, X, Volume2, Zap, Wind, Gauge, Menu, Sunrise, Sun, Sunset, Moon, Thermometer, Building2, Glasses } from "lucide-react";
import TabSeal from "./TabSeal";
import { handleColor, readoutsFor } from "../lib/storm";

const CAT_TICKS = ["TS", "C1", "C2", "C3", "C4", "C5"];
const TIME_META = [
  { id: "morning", label: "Morning", Icon: Sunrise },
  { id: "afternoon", label: "Afternoon", Icon: Sun },
  { id: "evening", label: "Evening", Icon: Sunset },
  { id: "night", label: "Night", Icon: Moon },
];

export default function Landing({ stage, onEnter, a11y, setA11y, intensity = 3, setIntensity, featured, onSelectEvent, featuredSystems = [], onSelectSystem, onDiveSystem, events = [], xrSupported = false, onEnterVR, timeOfDay = "afternoon", setTimeOfDay }) {
  const [panel, setPanel] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [archiveQ, setArchiveQ] = useState("");
  const r = readoutsFor(intensity);
  const filteredEvents = events.filter((e) => !archiveQ || `${e.name} ${e.type} ${e.year}`.toLowerCase().includes(archiveQ.toLowerCase()));

  if (stage === "splash") {
    return (
      <div className="splash" data-testid="splash-screen">
        <div className="splash-inner">
          <TabSeal size={130} />
          <div className="splash-kicker">Spatial Computing Report</div>
          <h1 className="splash-title">The Anatomy of a Hurricane, Nor'easter, and Blizzard</h1>
          <div className="splash-rule" />
          <div className="splash-sub">The Atmospheric Bureau · 25<sup>th</sup> Anniversary</div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing" data-testid="landing-screen">
      {/* Centered gold seal */}
      <div className="hud-seal" data-testid="landing-seal"><TabSeal size={64} /></div>

      {/* Top-left report menu trigger */}
      <div className="hud-corner-tl">
        <button className="icon-btn" data-testid="report-menu-btn" onClick={() => setDrawer((d) => !d)} title="Report Menu"><Menu size={18} /></button>
      </div>

      {/* Slide-out Report drawer */}
      <div className={`drawer ${drawer ? "open" : ""}`} data-testid="report-drawer">
        <div className="drawer-h">
          <span>The Report</span>
          <button className="a11y-x" onClick={() => setDrawer(false)} data-testid="drawer-close"><X size={18} /></button>
        </div>
        <div className="drawer-body">
          <div className="report-intro">
            <div className="brand-kicker">The Atmospheric Bureau · 25<sup>th</sup> Anniversary</div>
            <h1 className="brand-title">Witness the Machinery of the Sky</h1>
            <div className="brand-sub">A Spatial Computing Report · The Anatomy of a Hurricane, Nor'easter &amp; Blizzard</div>
          </div>
          {featuredSystems.length > 0 && (
            <div className="featured-systems" data-testid="featured-systems">
              <div className="fs-h">Most Recent Systems</div>
              {featuredSystems.map((s) => {
                const sst = s.live?.sea_surface_temp_c ?? s.sstFallback;
                return (
                  <div key={s.id} className="landing-hero system" data-testid={`featured-system-${s.id}`}>
                    <div className="fs-tap" role="button" tabIndex={0} data-testid={`featured-select-${s.id}`} onClick={() => onSelectSystem && onSelectSystem(s)}>
                      <div className="lh-kicker"><span className={`dot ${s.kind}`} /> {s.kind === "warm" ? "Warm-core Hurricane" : "Cold-core Nor'easter"}</div>
                      <div className="lh-title">{s.name}</div>
                      <div className="lh-year">{s.subtitle}</div>
                      {s.live && (
                        <div className="lh-stats">
                          <span><Wind size={12} /> {s.live.sustained_wind_mph} mph</span>
                          <span><Gauge size={12} /> {s.live.central_pressure_mb} mb</span>
                        </div>
                      )}
                      <div className={`sst-chip ${s.kind}`} data-testid={`featured-sst-${s.id}`}>
                        <Thermometer size={11} /> SST {Math.round(sst)}°C · {s.kind === "warm" ? "Warm ocean fuel" : "Cold shelf water"}
                      </div>
                    </div>
                    <button className="fs-dive" data-testid={`featured-dive-${s.id}`} onClick={() => onDiveSystem && onDiveSystem(s)}>
                      <Building2 size={12} /> Dive to {s.landfallCity ? s.landfallCity.name : "landfall"} →
                    </button>
                  </div>
                );
              })}
              {events.length > 0 && (
                <div className="fs-archive" data-testid="landing-archive">
                  <div className="fs-sub">Historical Archive · {events.length} Storms</div>
                  <input className="scen-input archive-search" data-testid="landing-archive-search" value={archiveQ} placeholder="Search storms by name, type or year…" onChange={(e) => setArchiveQ(e.target.value)} />
                  <div className="archive-scroll" data-testid="landing-archive-scroll">
                    {filteredEvents.map((ev) => (
                      <button key={ev.id} className="event-btn" data-testid={`landing-event-${ev.id}`} onClick={() => onSelectEvent && onSelectEvent(ev)}>
                        <strong>{ev.name}</strong><span>{ev.year} · {ev.type} · Peak {ev.peak_wind_mph} mph</span>
                      </button>
                    ))}
                    {filteredEvents.length === 0 && <div className="fs-empty" data-testid="landing-archive-empty">No storms match “{archiveQ}”.</div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom-left console */}
      <div className="hud-quad-bl landing-console" data-testid="landing-console">
        <div className="landing-eyebrow">An immersive 3D &amp; WebXR study of Earth's most powerful storms</div>

        <div className="landing-slider" data-testid="landing-intensity">
          <div className="ls-label">Storm Intensity · {r.label}</div>
          <input className="h-slider mini" style={{ "--handle": handleColor(intensity) }} type="range" min="0" max="5" step="0.01"
            value={intensity} onChange={(e) => setIntensity && setIntensity(parseFloat(e.target.value))} data-testid="landing-intensity-slider" />
          <div className="cat-ticks">{CAT_TICKS.map((t) => <span key={t}>{t}</span>)}</div>
        </div>

        <div className="segmented sm time" data-testid="landing-time-menu">
          {TIME_META.map(({ id, label, Icon }) => (
            <button key={id} className={`seg-btn ${timeOfDay === id ? "active" : ""}`} data-testid={`landing-time-${id}`} onClick={() => setTimeOfDay && setTimeOfDay(id)}><Icon size={13} /> {label}</button>
          ))}
        </div>

        <div className="landing-btns">
          <button className="gold-btn xl solid" data-testid="enter-immersive-btn" onClick={onEnter}><Sparkles size={17} /> Enter Immersive View</button>
          {xrSupported && (
            <button className="gold-btn xl vr" data-testid="landing-enter-vr-btn" onClick={onEnterVR}><Glasses size={17} /> Enter VR</button>
          )}
          <button className="gold-btn xl" data-testid="accessibility-btn" onClick={() => setPanel(true)}><Accessibility size={17} /> Accessibility</button>
        </div>
      </div>

      {panel && (
        <div className="a11y-overlay" onClick={() => setPanel(false)}>
          <div className="a11y-panel" onClick={(e) => e.stopPropagation()} data-testid="accessibility-panel">
            <div className="a11y-h">
              <span>Accessibility</span>
              <button className="a11y-x" onClick={() => setPanel(false)} data-testid="a11y-close"><X size={16} /></button>
            </div>
            <label className="a11y-row" data-testid="a11y-reduced-motion">
              <span><Zap size={14} /> Reduce Motion &amp; Particles</span>
              <input type="checkbox" checked={a11y.reducedMotion} onChange={(e) => setA11y((s) => ({ ...s, reducedMotion: e.target.checked }))} />
            </label>
            <label className="a11y-row" data-testid="a11y-mute">
              <span><Volume2 size={14} /> Mute All Audio</span>
              <input type="checkbox" checked={a11y.mute} onChange={(e) => setA11y((s) => ({ ...s, mute: e.target.checked }))} />
            </label>
            <p className="a11y-note">Reduced motion lowers particle density and disables idle camera drift for comfort.</p>
          </div>
        </div>
      )}
    </div>
  );
}
